import { FC, ReactNode } from 'react';
import { Box, VStack, Text } from '@chakra-ui/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import {
  FiList,
  FiHeart,
  FiBriefcase,
  FiGitBranch,
  FiZap,
  FiSettings,
  FiClock,
  FiUsers,
  FiHome,
  FiBookOpen,
} from 'react-icons/fi';

interface NavItemProps {
  icon: ReactNode;
  label: string;
  path: string;
  isActive: boolean;
  onClick: () => void;
}

const NavItem: FC<NavItemProps> = ({ icon, label, isActive, onClick }) => {
  return (
    <Box
      as="button"
      w="full"
      px={4}
      py={3}
      display="flex"
      alignItems="center"
      gap={3}
      borderRadius="lg"
      bg={isActive ? 'sidebar.item.active' : 'transparent'}
      color={isActive ? 'blue.600' : 'gray.700'}
      fontWeight={isActive ? 'semibold' : 'medium'}
      cursor="pointer"
      transition="all 0.2s"
      _hover={{
        bg: isActive ? 'sidebar.item.active' : 'sidebar.item.hover',
      }}
      onClick={onClick}
    >
      <Box fontSize="xl">{icon}</Box>
      <Text fontSize="sm">{label}</Text>
    </Box>
  );
};

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

/**
 * サイドバーナビゲーションコンポーネント
 * 常時表示（デスクトップ）/ ドロワー（モバイル）
 */
export const Sidebar: FC<SidebarProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();

  const navItems = [
    {
      icon: <FiList />,
      label: '個体管理',
      path: '/dogs',
    },
    {
      icon: <FiHeart />,
      label: '出産記録',
      path: '/birth-records',
    },
    {
      icon: <FiBriefcase />,
      label: '交配記録',
      path: '/mating-records',
    },
    {
      icon: <FiGitBranch />,
      label: '血統図',
      path: '/pedigree',
    },
    {
      icon: <FiZap />,
      label: 'AI交配提案',
      path: '/ai-mating',
    },
    {
      icon: <FiSettings />,
      label: 'プロンプト管理',
      path: '/admin/prompts',
    },
    {
      icon: <FiClock />,
      label: 'AI分析履歴',
      path: '/admin/analysis-history',
    },
    // 管理者のみ表示
    ...(user?.role === 'admin'
      ? [
          {
            icon: <FiUsers />,
            label: 'アカウント管理',
            path: '/admin/accounts',
          },
          {
            icon: <FiHome />,
            label: '事業者管理',
            path: '/admin/business',
          },
        ]
      : []),
    // 操作マニュアル（全ユーザー）。参照用のため最後に置く
    {
      icon: <FiBookOpen />,
      label: '操作マニュアル',
      path: '/manual',
    },
  ];

  const handleNavClick = (path: string) => {
    navigate(path);
  };

  return (
    <Box
      as="nav"
      w="60"
      h="full"
      bg="sidebar.bg"
      borderRightWidth="1px"
      borderRightColor="sidebar.border"
      position="fixed"
      left="0"
      top="16"
      bottom="0"
      overflowY="auto"
      display={{ base: 'none', lg: 'block' }}
      shadow="sm"
    >
      <VStack gap={2} p={4} align="stretch">
        {navItems.map((item) => (
          <NavItem
            key={item.path}
            icon={item.icon}
            label={item.label}
            path={item.path}
            isActive={
              location.pathname === item.path ||
              (item.path !== '/' && location.pathname.startsWith(item.path))
            }
            onClick={() => handleNavClick(item.path)}
          />
        ))}
      </VStack>
    </Box>
  );
};

/**
 * モバイル用ドロワーサイドバーコンポーネント
 */
export const MobileSidebar: FC<SidebarProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();

  const navItems = [
    {
      icon: <FiList />,
      label: '個体管理',
      path: '/dogs',
    },
    {
      icon: <FiHeart />,
      label: '出産記録',
      path: '/birth-records',
    },
    {
      icon: <FiBriefcase />,
      label: '交配記録',
      path: '/mating-records',
    },
    {
      icon: <FiGitBranch />,
      label: '血統図',
      path: '/pedigree',
    },
    {
      icon: <FiZap />,
      label: 'AI交配提案',
      path: '/ai-mating',
    },
    {
      icon: <FiSettings />,
      label: 'プロンプト管理',
      path: '/admin/prompts',
    },
    {
      icon: <FiClock />,
      label: 'AI分析履歴',
      path: '/admin/analysis-history',
    },
    // 管理者のみ表示
    ...(user?.role === 'admin'
      ? [
          {
            icon: <FiUsers />,
            label: 'アカウント管理',
            path: '/admin/accounts',
          },
          {
            icon: <FiHome />,
            label: '事業者管理',
            path: '/admin/business',
          },
        ]
      : []),
    // 操作マニュアル（全ユーザー）。参照用のため最後に置く
    {
      icon: <FiBookOpen />,
      label: '操作マニュアル',
      path: '/manual',
    },
  ];

  const handleNavClick = (path: string) => {
    navigate(path);
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* オーバーレイ */}
      <Box
        position="fixed"
        inset="0"
        bg="blackAlpha.600"
        zIndex="20"
        display={{ base: 'block', lg: 'none' }}
        onClick={onClose}
      />

      {/* ドロワー */}
      <Box
        as="nav"
        w="64"
        h="full"
        bg="sidebar.bg"
        position="fixed"
        left="0"
        top="0"
        bottom="0"
        zIndex="30"
        overflowY="auto"
        display={{ base: 'block', lg: 'none' }}
        shadow="xl"
        animation="slideIn 0.2s ease-out"
      >
        <VStack gap={2} p={4} align="stretch" mt="16">
          {navItems.map((item) => (
            <NavItem
              key={item.path}
              icon={item.icon}
              label={item.label}
              path={item.path}
              isActive={
                location.pathname === item.path ||
                (item.path !== '/' && location.pathname.startsWith(item.path))
              }
              onClick={() => handleNavClick(item.path)}
            />
          ))}
        </VStack>
      </Box>
    </>
  );
};
